/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { View, TouchableOpacity, ScrollView } from 'react-native';
import Toast from 'react-native-toast-message';
import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import ModalHalf from '@components/modal/ModalHalf';
import ModalTopRounded from '@components/modal/ModalTopRounded';
import DatePicker from '@components/form/DatePicker';
import StatusBadge from '@components/global/StatusBadge';
import Image from '@components/global/Image';
import PromotionAccountCreateOption from './PromotionAccountCreateOption';
import * as Layout from '@components/screen/Layout';

import useApi from '@/hooks/useApi';
import useListScreen from '@/hooks/useListScreen';
import { MsB2b } from '@api/ms-b2b';
import {
  useGetPromotionAccountListQuery,
  useGetPromotionAccountCountQuery,
  useGetPromotionAccountInfiniteListInfiniteQuery,
} from '@api/listApi';
import { showPopup } from '@/store/modal';
import { hasNoAccess, exportCsvApi } from '@global/helpers';
import { formatPeriod, formatDate } from './utils';

const tableStructure = {
  name: {
    head: 'Name',
    body: 'name',
    textStyle: { fontFamily: 'figtree-medium' },
    headStyle: { textAlign: 'left' },
  },
  account_group: {
    head: 'Account Group',
    body: 'account_group',
    type: 'slot',
  },
  total_products: {
    head: 'Total Product',
    body: 'total_products',
    type: 'slot',
  },
  period: {
    head: 'Periode',
    body: 'period',
    type: 'slot',
  },
  status: {
    head: 'Status',
    body: 'is_active',
    type: 'status_active',
  },
  updated_at: {
    head: 'Last Update, by',
    body: 'updated_at',
    type: 'slot',
  },
};

// Search box writes `name` (→ $regex); newest-first sort is baked into the resource.
const toApiFilter = (raw = {}) => {
  const filter = {};
  const name = raw.name?.$regex ?? raw.name;
  if (name) {
    filter.name = { $regex: name, $options: 'i' };
  }
  return filter;
};

const mapRow = (item) => ({ ...item, id: item._id });

const PromotionAccountScreen = (props) => {
  const dispatch = useDispatch();

  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetPromotionAccountListQuery,
    countHook: useGetPromotionAccountCountQuery,
    infiniteHook: useGetPromotionAccountInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });
  const deleteApi = useApi(MsB2b.deletePromotionAccount);

  const [selectedItem, setSelectedItem] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFilter, setExportFilter] = useState({
    from_date: '',
    to_date: '',
  });
  const [exporting, setExporting] = useState(false);

  const hasAccess = !hasNoAccess([
    'superadmin',
    'sales_admin',
    'marketing_admin',
    'general',
    'promotor',
  ]);

  const searchValue = useMemo(() => {
    const k = params.filter?.name;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map(mapRow);
  }, [list.data]);

  const goToDetail = ({ data }) => {
    openChild('PromotionAccountFormScreen', {
      titleScreen: data.name,
      id: data._id,
      viewMode: true,
    });
  };

  const handleEdit = () => {
    setShowOptions(false);
    openChild('PromotionAccountFormScreen', {
      titleScreen: selectedItem?.name,
      id: selectedItem?._id,
    });
  };

  const handleDelete = async () => {
    setShowOptions(false);
    try {
      await deleteApi.request(selectedItem?._id);
      Toast.show({ text1: 'Deleted successfully.', type: 'success' });
      list.refetch();
    } catch (error) {
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    }
  };

  const handleExport = async () => {
    if (!exportFilter.from_date || !exportFilter.to_date) {
      Toast.show({
        text1: 'Please select a period before exporting.',
        type: 'error',
      });
      return;
    }
    setExporting(true);
    await exportCsvApi(
      'promotion-accounts',
      {
        period: {
          $gte: exportFilter.from_date,
          $lte: exportFilter.to_date,
        },
      },
      { isDownload: false, message: 'Export will be sent to your email.' },
    );
    setExporting(false);
    setShowExportModal(false);
    setExportFilter({ from_date: '', to_date: '' });
  };

  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title title="Promotion Account">
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Button
                type="secondary"
                rounded={true}
                buttonStyle={{
                  width: 90,
                  marginRight: 8,
                  backgroundColor: Colors.white,
                  borderWidth: 1,
                  borderColor: Colors.blue0,
                }}
                title={
                  <SociollaText
                    type="medium"
                    style={{ fontSize: 14, color: Colors.blue0 }}>
                    Export
                  </SociollaText>
                }
                onPress={() => setShowExportModal(true)}
              />
              {hasAccess ? (
                <Button
                  type="primary"
                  rounded={true}
                  buttonStyle={{ width: 100 }}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      + Add
                    </SociollaText>
                  }
                  onPress={() =>
                    dispatch(
                      showPopup({
                        name: 'promotionAccountCreateOption',
                        value: true,
                      }),
                    )
                  }
                />
              ) : null}
            </View>
          </Layout.Title>

          <Table
            filterName={scope}
            structures={tableStructure}
            data={rows}
            loading={list.isLoading}
            refetching={list.isFetching}
            hasCallback={true}
            hasNoHide={true}
            totalData={count.data}
            skip={params.skip}
            limit={params.limit}
            showFilter={false}
            searchKey="name"
            searchPlaceholder="Search by name..."
            onClickRow={(val) => goToDetail(val)}
            searchValue={searchValue}
            slot={{
              account_group: ({ data }) => (
                <SociollaText>
                  {data?.account_group?.name ||
                    data?.account_group?.code ||
                    '-'}
                </SociollaText>
              ),
              total_products: ({ data }) => (
                <SociollaText>{data?.products?.length ?? 0}</SociollaText>
              ),
              period: ({ data }) => (
                <SociollaText>
                  {formatPeriod(data?.period, data?.period_type, data?.dates)}
                </SociollaText>
              ),
              updated_at: ({ data }) => {
                const date = data?.updated_at
                  ? formatDate(data.updated_at)
                  : '-';
                const by = data?.updated_by?.name || '';
                return (
                  <SociollaText style={{ fontSize: 12 }}>
                    {by ? `${date} | ${by}` : date}
                  </SociollaText>
                );
              },
            }}
          />
        </>
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="name"
          placeholder="Search by name"
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          skip={params.skip}
          limit={params.limit}
          data={rows}
          totalData={count.data}
          searchValue={searchValue}
          renderItem={({ data, index }) => {
            const updatedDate = data?.updated_at
              ? formatDate(data.updated_at)
              : '-';
            const updatedBy = data?.updated_by?.name || '';
            return (
              <Layout.ItemMobile
                key={index}
                column={true}
                touchable={true}
                style={{ alignItems: 'flex-start' }}
                onPress={() => goToDetail({ data })}>
                {/* Row 1: Name + Status badge + options */}
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginBottom: 10,
                    width: '100%',
                  }}>
                  <SociollaText type="medium" style={{ flex: 1 }}>
                    {data?.name || '-'}
                  </SociollaText>
                  <StatusBadge
                    status={data?.is_active ? 'active' : 'in_active'}
                  />
                  {hasAccess ? (
                    <TouchableOpacity
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      style={{ paddingLeft: 8 }}
                      onPress={(e) => {
                        if (e?.stopPropagation) {
                          e.stopPropagation();
                        }
                        setSelectedItem(data);
                        setShowOptions(true);
                      }}>
                      <SociollaText
                        style={{
                          fontSize: 20,
                          color: Colors.grey1,
                          lineHeight: 20,
                        }}>
                        ⋮
                      </SociollaText>
                    </TouchableOpacity>
                  ) : null}
                </View>
                {/* 2-column grid: labels row then values row */}
                <View
                  style={{
                    flexDirection: 'row',
                    marginBottom: 2,
                    width: '100%',
                  }}>
                  <SociollaText
                    style={{
                      flex: 1,
                      fontSize: 11,
                      color: Colors.grey1,
                      marginRight: 16,
                    }}>
                    Account Group
                  </SociollaText>
                  <SociollaText
                    style={{ flex: 1, fontSize: 11, color: Colors.grey1 }}>
                    Total Product
                  </SociollaText>
                </View>
                <View
                  style={{
                    flexDirection: 'row',
                    marginBottom: 8,
                    width: '100%',
                  }}>
                  <SociollaText
                    style={{ flex: 1, fontSize: 12, marginRight: 16 }}
                    numberOfLines={1}>
                    {data?.account_group?.name ||
                      data?.account_group?.code ||
                      '-'}
                  </SociollaText>
                  <SociollaText style={{ flex: 1, fontSize: 12 }}>
                    {data?.products?.length ?? 0}
                  </SociollaText>
                </View>
                <View
                  style={{
                    flexDirection: 'row',
                    marginBottom: 2,
                    width: '100%',
                  }}>
                  <SociollaText
                    style={{
                      flex: 1,
                      fontSize: 11,
                      color: Colors.grey1,
                      marginRight: 16,
                    }}>
                    Periode
                  </SociollaText>
                  <SociollaText
                    style={{ flex: 1, fontSize: 11, color: Colors.grey1 }}>
                    Last Update, by
                  </SociollaText>
                </View>
                <View style={{ flexDirection: 'row', width: '100%' }}>
                  <View
                    style={{
                      flex: 1,
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginRight: 16,
                    }}>
                    <Image
                      width={14}
                      height={14}
                      style={{ width: 14, height: 14, marginRight: 4 }}
                      svg={true}
                      source={require('@assets/img/icons/menu/calendar.svg')}
                    />
                    <SociollaText
                      style={{ fontSize: 12, flex: 1 }}
                      numberOfLines={1}>
                      {formatPeriod(
                        data?.period,
                        data?.period_type,
                        data?.dates,
                      )}
                    </SociollaText>
                  </View>
                  <SociollaText
                    style={{ flex: 1, fontSize: 12 }}
                    numberOfLines={1}>
                    {updatedBy ? `${updatedDate} | ${updatedBy}` : updatedDate}
                  </SociollaText>
                </View>
              </Layout.ItemMobile>
            );
          }}
        />
      )}

      <ModalHalf
        title={selectedItem?.name || 'Options'}
        visible={showOptions}
        onClose={() => setShowOptions(false)}>
        <View style={{ paddingHorizontal: 15, paddingBottom: 20 }}>
          <TouchableOpacity
            style={{
              paddingVertical: 15,
              borderBottomWidth: 1,
              borderBottomColor: Colors.grey0,
            }}
            onPress={handleEdit}>
            <SociollaText>Edit</SociollaText>
          </TouchableOpacity>
          <TouchableOpacity
            style={{ paddingVertical: 15 }}
            onPress={handleDelete}>
            <SociollaText style={{ color: Colors.red0 }}>Delete</SociollaText>
          </TouchableOpacity>
        </View>
      </ModalHalf>

      <ModalTopRounded
        title="Export Promotion Account"
        noTranslate={true}
        visible={showExportModal}
        maxWidth={isWeb ? 420 : '100%'}
        onClose={() => {
          setShowExportModal(false);
          setExportFilter({ from_date: '', to_date: '' });
        }}>
        <ScrollView>
          <View
            style={{
              paddingHorizontal: 20,
              paddingTop: 20,
              paddingBottom: 10,
            }}>
            <SociollaText
              style={{ fontSize: 13, color: Colors.grey1, marginBottom: 16 }}>
              Select a period to export. The result will be sent to your email.
            </SociollaText>
            <Layout.FormGroup>
              <DatePicker
                label="From"
                isRequired={true}
                value={exportFilter.from_date}
                onChange={(val) =>
                  setExportFilter((prev) => ({ ...prev, from_date: val }))
                }
                placeholder="Select start date"
              />
            </Layout.FormGroup>
            <Layout.FormGroup>
              <DatePicker
                label="To"
                isRequired={true}
                value={exportFilter.to_date}
                onChange={(val) =>
                  setExportFilter((prev) => ({ ...prev, to_date: val }))
                }
                placeholder="Select end date"
                minDate={exportFilter.from_date}
              />
            </Layout.FormGroup>
          </View>
        </ScrollView>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'flex-end',
            paddingHorizontal: 20,
            paddingVertical: 16,
            borderTopWidth: 1,
            borderTopColor: Colors.grey0,
          }}>
          <Button
            rounded={true}
            buttonStyle={{
              width: 90,
              marginRight: 10,
              backgroundColor: Colors.white,
              borderWidth: 1,
              borderColor: Colors.grey0,
            }}
            title={<SociollaText style={{ fontSize: 14 }}>Cancel</SociollaText>}
            onPress={() => {
              setShowExportModal(false);
              setExportFilter({ from_date: '', to_date: '' });
            }}
          />
          <Button
            rounded={true}
            buttonStyle={{ width: 100 }}
            loading={exporting}
            title={
              <SociollaText
                type="bold"
                style={{ fontSize: 14, color: Colors.white }}>
                Export
              </SociollaText>
            }
            onPress={handleExport}
          />
        </View>
      </ModalTopRounded>

      <PromotionAccountCreateOption
        navigation={props.navigation}
        openChild={openChild}
        onImportSuccess={() => list.refetch()}
      />
    </Layout.Container>
  );
};

export default PromotionAccountScreen;
